import { useLayoutEffect, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import {
	distanceFromBottom,
	FOLLOW_TIME_CONSTANT_MS,
	followStep,
	isAwayFromLatest,
	nextStickToBottom,
} from "./chat-scroll"

// Any scroll up releases the follow; scrolling back to the end, or a new
// followKey (the reader's own message), picks it up again.
export const useStickToBottom = ({
	followKey,
	followTimeConstantMs = FOLLOW_TIME_CONSTANT_MS,
	startAtTop = false,
}: {
	followKey: string | null | undefined
	followTimeConstantMs?: number
	// A document opened to be read, not a conversation, starts at its first line.
	startAtTop?: boolean
}) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)
	const [isAwayFromBottom, setIsAwayFromBottom] = useState(false)
	const isStuckRef = useRef(!startAtTop)
	// Held at the first line until the reader scrolls, so content that loads or
	// focuses inside the document never drags it down on open.
	const pinnedToTopRef = useRef(startAtTop)
	const openingFollowKeyRef = useRef(followKey)
	const lastScrollTopRef = useRef(0)
	const frameRef = useRef<number | null>(null)
	const hasSettledRef = useRef(false)
	const reduceMotion = useReducedMotion()
	const reduceMotionRef = useRef(reduceMotion)
	reduceMotionRef.current = reduceMotion
	const timeConstantRef = useRef(followTimeConstantMs)
	timeConstantRef.current = followTimeConstantMs

	const stopFollowing = () => {
		if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
		frameRef.current = null
	}

	const followToBottom = () => {
		const el = containerRef.current
		if (!el) return
		// Opening a saved chat, or a gap over a screen tall (history arriving,
		// sending from far up), jumps: gliding past that much text is a blur.
		if (
			!hasSettledRef.current ||
			reduceMotionRef.current ||
			distanceFromBottom(el) > el.clientHeight
		) {
			stopFollowing()
			hasSettledRef.current = true
			el.scrollTop = el.scrollHeight - el.clientHeight
			lastScrollTopRef.current = el.scrollTop
			return
		}
		if (frameRef.current !== null || distanceFromBottom(el) === 0) return
		let previous: number | null = null
		const tick = (now: number) => {
			frameRef.current = null
			if (!isStuckRef.current) return
			const before = el.scrollTop
			const gap = el.scrollHeight - el.clientHeight - before
			const step = followStep(
				gap,
				previous === null ? 16 : now - previous,
				timeConstantRef.current,
			)
			previous = now
			if (step <= 0) return
			el.scrollTop = before + step
			if (el.scrollTop <= before) return
			frameRef.current = requestAnimationFrame(tick)
		}
		frameRef.current = requestAnimationFrame(tick)
	}

	// Layout effects, so an opened conversation never paints at the top first.
	useLayoutEffect(() => {
		if (!followKey) return
		// Opening a stored chat that ends on the reader's message is no new message.
		if (pinnedToTopRef.current && followKey === openingFollowKeyRef.current)
			return
		pinnedToTopRef.current = false
		isStuckRef.current = true
		setIsAwayFromBottom(false)
		followToBottom()
	}, [followKey])

	useLayoutEffect(() => {
		const el = containerRef.current
		const content = contentRef.current
		if (!el || !content) return

		if (pinnedToTopRef.current) el.scrollTop = 0
		const sizeObserver = new ResizeObserver(() => {
			if (pinnedToTopRef.current) {
				el.scrollTop = 0
				return
			}
			if (isStuckRef.current) followToBottom()
			// A reply growing while the reader is above it moves the latest
			// message away without a scroll event of its own.
			setIsAwayFromBottom(!isStuckRef.current && isAwayFromLatest(el))
		})
		sizeObserver.observe(content)
		sizeObserver.observe(el)

		const onScroll = () => {
			if (pinnedToTopRef.current) {
				if (el.scrollTop !== 0) el.scrollTop = 0
				return
			}
			isStuckRef.current = nextStickToBottom({
				isStuck: isStuckRef.current,
				previousScrollTop: lastScrollTopRef.current,
				scrollTop: el.scrollTop,
				distance: distanceFromBottom(el),
			})
			lastScrollTopRef.current = el.scrollTop
			if (!isStuckRef.current) stopFollowing()
			setIsAwayFromBottom(!isStuckRef.current && isAwayFromLatest(el))
		}
		// The wheel fires before the follow can swallow a small trackpad nudge.
		const onWheel = (event: WheelEvent) => {
			pinnedToTopRef.current = false
			if (event.deltaY >= 0 || el.scrollHeight <= el.clientHeight) return
			isStuckRef.current = false
			stopFollowing()
		}
		// Any deliberate input hands the scroll back to the reader.
		const release = () => {
			pinnedToTopRef.current = false
		}
		el.addEventListener("scroll", onScroll, { passive: true })
		el.addEventListener("wheel", onWheel, { passive: true })
		el.addEventListener("touchstart", release, { passive: true })
		el.addEventListener("keydown", release)
		el.addEventListener("pointerdown", release)

		return () => {
			sizeObserver.disconnect()
			el.removeEventListener("scroll", onScroll)
			el.removeEventListener("wheel", onWheel)
			el.removeEventListener("touchstart", release)
			el.removeEventListener("keydown", release)
			el.removeEventListener("pointerdown", release)
			stopFollowing()
		}
	}, [])

	const scrollToLatest = () => {
		const el = containerRef.current
		if (!el) return
		pinnedToTopRef.current = false
		isStuckRef.current = true
		setIsAwayFromBottom(false)
		el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
	}

	return { containerRef, contentRef, isAwayFromBottom, scrollToLatest }
}
